import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ScrollReveal from '@/components/ScrollReveal';
import {
  Workflow,
  UserCog,
  Database,
  TrendingUp,
  Landmark,
  Pill,
  Cloud,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

interface FeatureItem {
  title: string;
  desc: string;
}

function isFeatureItem(item: unknown): item is FeatureItem {
  return typeof item === 'object' && item !== null && 'title' in item && 'desc' in item;
}

const capabilities = [
  {
    id: 'workflow-automation',
    icon: Workflow,
    title: 'AI Workflow Automation',
    subtitle: 'End-to-end automation of business workflows',
    description:
      'Orchestrate AI components (LLMs/SLMs, retrieval, classification, extraction, decision support) with controls and observability.',
    image: '/images/ai-workflow.jpg',
    useCases: [
      'CV ingestion, ranking, interview preparation, policy Q&A',
      'Incident triage, summarisation, escalation routing',
      'Document processing, evidence collection, reporting',
      'Research synthesis, brief creation, insight extraction',
    ],
    deliverables: [
      'A workflow map + ROI model',
      'A working workflow prototype with guardrails',
      'Integration plan + rollout plan',
      'Monitoring and evaluation approach',
    ],
  },
  {
    id: 'executive-assistants',
    icon: UserCog,
    title: 'Executive AI Assistants',
    subtitle: 'CEO / CFO / CTO / CIO',
    description:
      'Secure assistants designed for executive contexts: briefing packs, KPIs, strategic synthesis, and decision support with strict governance.',
    image: '/images/executive-ai.jpg',
    outputs: [
      'Board packs and leadership briefs',
      'KPI and operational synthesis',
      '"What changed?" and "What matters?" analysis',
      'Risk summaries and action recommendations (with traceability)',
    ],
    governance: [
      { title: 'RBAC and policy controls', desc: 'Role-based access with fine-grained permissions' },
      { title: 'Audit trails', desc: 'Source grounding and full traceability' },
      { title: 'Evaluation workflows', desc: 'Red-teaming and continuous quality assessment' },
      { title: 'Safe escalation', desc: 'Human-in-the-loop controls and guardrails' },
    ],
  },
  {
    id: 'rag-knowledge',
    icon: Database,
    title: 'Enterprise RAG & Knowledge Systems',
    subtitle: 'Retrieval and knowledge systems with security controls',
    description:
      'Enterprise-grade retrieval and knowledge systems that connect your internal content to assistants and workflows — with security, accuracy and operational controls.',
    image: '/images/enterprise-rag.jpg',
    features: [
      { title: 'Document ingestion and normalisation', desc: 'Handle diverse document types with robust preprocessing' },
      { title: 'Chunking, embeddings, and retrieval optimisation', desc: 'Tuned for accuracy and performance' },
      { title: 'Source grounding + citations', desc: 'Every answer traced back to source documents' },
      { title: 'Guardrails and role-based access', desc: 'Secure, policy-driven information access' },
      { title: 'Observability', desc: 'Cost/latency/quality monitoring and optimisation' },
    ],
  },
  {
    id: 'marketing-intelligence',
    icon: TrendingUp,
    title: 'AI Marketing & Growth Intelligence',
    subtitle: 'Content generation and analytics for marketing teams',
    description:
      'AI systems that support modern marketing teams with content generation, insights, analytics, and exec-ready reporting — connected to your CRM and dashboards.',
    image: '/images/marketing-ai.jpg',
    useCases: [
      { title: 'Campaign content pipelines', desc: 'Generate marketing content at scale with brand controls and approval workflows' },
      { title: 'Customer insight extraction', desc: 'Synthesise insights from calls, support tickets, and CRM notes' },
      { title: 'Funnel performance summaries', desc: 'Executive dashboards with automated KPI reporting' },
      { title: 'Competitive synthesis', desc: 'Market intelligence and messaging consistency checks' },
    ],
  },
  {
    id: 'fintech-regulatory',
    icon: Landmark,
    title: 'FinTech & Regulatory AI',
    subtitle: 'AI for regulated financial services workflows',
    description:
      'AI for regulated financial services workflows — designed for auditability, traceability, and controlled operation.',
    image: '/images/fintech-ai.jpg',
    useCases: [
      'Document intelligence and extraction: Automated processing of financial documents with high accuracy',
      'Policy-driven compliance checks: Validate transactions and operations against regulatory rules',
      'Evidence compilation and reporting: Automated audit trail generation and regulatory reporting',
      'Executive summaries and risk dashboards: Board-ready risk and compliance reporting',
    ],
    regulatoryFocus:
      'Every system is built with audit trails, source grounding, and compliance requirements at the core — not added as an afterthought.',
  },
  {
    id: 'pharma-compliance',
    icon: Pill,
    title: 'Pharma Knowledge & Compliance AI',
    subtitle: 'Regulatory document processing and research synthesis',
    description:
      'AI workflows to support pharma teams with regulatory document processing, research synthesis, and controlled enterprise knowledge assistants.',
    image: '/images/pharma-ai.jpg',
    useCases: [
      { title: 'Regulatory and quality documentation assistants', desc: 'AI-powered access to SOPs, regulatory guidelines, and quality documentation' },
      { title: 'Research synthesis across controlled corpora', desc: 'Synthesise insights from research documents with strict access controls' },
      { title: 'SOP/policy assistants with access controls', desc: 'Role-based access to standard operating procedures' },
      { title: 'Audit-friendly summarisation', desc: 'All outputs fully traceable to source documents' },
    ],
  },
  {
    id: 'cloud-platforms',
    icon: Cloud,
    title: 'Cloud-Native Enterprise Platforms',
    subtitle: 'Deployment, security, CI/CD for enterprise scale',
    description:
      'The platform layer that makes AI work reliably at enterprise scale: deployment, security, CI/CD, and operational readiness.',
    image: '/images/cloud-platforms.jpg',
    capabilities: [
      { title: 'AWS/Azure/OpenShift/Kubernetes deployments', desc: 'Multi-cloud and hybrid deployment expertise' },
      { title: 'Secure service-to-service communication', desc: 'Zero-trust architectures and encrypted communication patterns' },
      { title: 'CI/CD automation and release governance', desc: 'Automated testing, deployment, and release management' },
      { title: 'Observability and SRE-ready operations', desc: 'Comprehensive monitoring, logging, and alerting' },
      { title: 'Multi-tenant and regulated deployment patterns', desc: 'Secure isolation and compliance-ready architectures' },
    ],
  },
];

export default function CapabilitiesPage() {
  useEffect(() => {
    ScrollTrigger.refresh();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="pt-44 pb-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
          <div className="container-custom relative z-10">
            <ScrollReveal className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                Capabilities
              </h1>
              <p className="text-lg sm:text-xl text-slate-600">
                Production-grade AI systems built for enterprise scale, security
                and compliance
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Capabilities Navigation */}
        <section className="py-6 bg-slate-50 border-y border-slate-200 sticky top-20 z-40">
          <div className="container-custom">
            <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {capabilities.map((cap) => (
                <button
                  key={cap.id}
                  onClick={() => {
                    const el = document.getElementById(cap.id);
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="whitespace-nowrap text-sm text-slate-600 hover:text-primary transition-colors px-4 py-2 rounded-lg hover:bg-primary/10"
                >
                  {cap.title}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Capabilities Content */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="space-y-24">
              {capabilities.map((cap, index) => (
                <div
                  key={cap.id}
                  id={cap.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Image */}
                  <ScrollReveal
                    direction={index % 2 === 0 ? 'left' : 'right'}
                    className={index % 2 === 1 ? 'lg:order-2' : ''}
                  >
                    <div className="relative rounded-xl overflow-hidden group shadow-card">
                      <img
                        src={cap.image}
                        alt={cap.title}
                        className="w-full h-80 object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
                      <div className="absolute bottom-6 left-6 w-14 h-14 bg-primary/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-primary/30">
                        <cap.icon className="w-7 h-7 text-primary" />
                      </div>
                    </div>
                  </ScrollReveal>

                  {/* Content */}
                  <ScrollReveal
                    direction={index % 2 === 0 ? 'right' : 'left'}
                    delay={0.2}
                    className={index % 2 === 1 ? 'lg:order-1' : ''}
                  >
                    <div>
                      <span className="text-primary text-sm font-medium uppercase tracking-wider">
                        {cap.subtitle}
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2 mb-4">
                        {cap.title}
                      </h2>
                      <p className="text-slate-600 leading-relaxed mb-6">
                        {cap.description}
                      </p>

                      {/* Use Cases / Features */}
                      {cap.useCases && Array.isArray(cap.useCases) && (
                        <div className="mb-6">
                          <h4 className="text-slate-900 font-semibold mb-3">
                            Use Cases
                          </h4>
                          <ul className="space-y-2">
                            {cap.useCases.map((useCase, i) => {
                              const uc = useCase as string | FeatureItem;
                              const isObject = isFeatureItem(uc);
                              return (
                                <li
                                  key={i}
                                  className="flex items-start gap-2 text-slate-600 text-sm"
                                >
                                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                  {isObject ? (
                                    <span>
                                      <strong className="text-slate-900">
                                        {uc.title}:
                                      </strong>{' '}
                                      {uc.desc}
                                    </span>
                                  ) : (
                                    <span>{uc as string}</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {/* Features */}
                      {cap.features && (
                        <div className="mb-6">
                          <h4 className="text-slate-900 font-semibold mb-3">
                            Features
                          </h4>
                          <ul className="space-y-3">
                            {cap.features.map((feature, i) => {
                              const f = feature as FeatureItem;
                              return (
                                <li key={i} className="text-sm">
                                  <strong className="text-slate-900">
                                    {f.title}
                                  </strong>
                                  <p className="text-slate-600">
                                    {f.desc}
                                  </p>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {/* Outputs */}
                      {cap.outputs && (
                        <div className="mb-6">
                          <h4 className="text-slate-900 font-semibold mb-3">
                            Typical Outputs
                          </h4>
                          <ul className="space-y-2">
                            {cap.outputs.map((output, i) => (
                              <li
                                key={i}
                                className="flex items-start gap-2 text-slate-600 text-sm"
                              >
                                <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                {output}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Governance */}
                      {cap.governance && (
                        <div className="mb-6">
                          <h4 className="text-slate-900 font-semibold mb-3">
                            Built for Governance
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {cap.governance.map((item, i) => (
                              <div
                                key={i}
                                className="bg-slate-50 rounded-lg p-4 border border-slate-200"
                              >
                                <h5 className="text-slate-900 font-medium text-sm mb-1">
                                  {item.title}
                                </h5>
                                <p className="text-slate-500 text-xs">
                                  {item.desc}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Capabilities */}
                      {cap.capabilities && (
                        <div className="mb-6">
                          <h4 className="text-slate-900 font-semibold mb-3">
                            Capabilities
                          </h4>
                          <ul className="space-y-3">
                            {cap.capabilities.map((capability, i) => (
                              <li key={i} className="text-sm">
                                <strong className="text-slate-900">
                                  {capability.title}
                                </strong>
                                <p className="text-slate-600">
                                  {capability.desc}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Deliverables */}
                      {cap.deliverables && (
                        <div className="mb-6">
                          <h4 className="text-slate-900 font-semibold mb-3">
                            What You Get
                          </h4>
                          <ul className="space-y-2">
                            {cap.deliverables.map((item, i) => (
                              <li
                                key={i}
                                className="flex items-start gap-2 text-slate-600 text-sm"
                              >
                                <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Regulatory Focus */}
                      {cap.regulatoryFocus && (
                        <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
                          <h4 className="text-primary font-semibold mb-2">
                            Regulatory Focus
                          </h4>
                          <p className="text-slate-600 text-sm">
                            {cap.regulatoryFocus}
                          </p>
                        </div>
                      )}
                    </div>
                  </ScrollReveal>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <ScrollReveal className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Ready to build production-grade AI?
              </h2>
              <p className="text-slate-600 mb-8">
                Book a discovery call to discuss your specific use-case and
                deployment requirements.
              </p>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2 group"
              >
                Book a Discovery Call
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
